import { useTranslations } from 'next-intl';
import {
  ContentLayout,
  GeneralPageContent,
} from '@silvertours/common-landingpages-view';
import {
  BenefitsLegacy,
  StageLegacy,
  ContentArticle,
  SerpOffersModule,
} from '@silvertours/front-features';
import { AcrissFilter } from '@silvertours/back-domain-offer';
import { Body } from '../body';

const GeneralContent = ({
  content: {
    article,
    globalRating,
    introduction,
    links,
    attributes: { island, airport, railStation, category, bookingAttribute },
    location,
    offersByCategory,
    stage,
    textContent: { primaryHeadline },
  },
  ...commonProps
}: ContentLayout<GeneralPageContent>) => {
  const t = useTranslations('features');
  const translations = t.raw('article' as any);
  const offersTranslations = useTranslations('features.offers');
  let destinationTranslation = offersTranslations('serp.title.destination');
  let defaultTitle = offersTranslations('serp.title.default');
  let searchButtonText = offersTranslations('searchButtonText');
  let searchQuery = '';

  if (island) {
    destinationTranslation = offersTranslations('serp.title.island');
  }

  if (category) {
    if (category.isSportscar) {
      searchQuery = new URLSearchParams({
        car_type: 'sportscar',
      }).toString();
    } else {
      let params = {};
      const filter = new AcrissFilter(category?.query);
      const filterParams = filter.getQueryParam();
      if (filterParams.length > 0) {
        filterParams.forEach(param => {
          params = { ...params, ...param };
          searchQuery = new URLSearchParams(params).toString();
        });
      }
    }
    destinationTranslation = offersTranslations(
      'serp.title.categoryDestination',
    ).replace('%CATEGORY%', category.name);
    defaultTitle = offersTranslations('serp.title.category').replace(
      '%CATEGORY%',
      category.name,
    );
    searchButtonText = offersTranslations('searchButtonTextCategory').replace(
      '%CATEGORY%',
      category.name,
    );
  }

  if (bookingAttribute) {
    let params = {};
    bookingAttribute.forEach(attribute => {
      params = { ...params, ...attribute.query };
      searchQuery = new URLSearchParams(params).toString();
      destinationTranslation = offersTranslations(
        'serp.title.categoryDestination',
      ).replace('%CATEGORY%', attribute.name);
      defaultTitle = offersTranslations('serp.title.category').replace(
        '%CATEGORY%',
        attribute.name,
      );
      searchButtonText = offersTranslations('searchButtonTextCategory').replace(
        '%CATEGORY%',
        attribute.name,
      );
    });
  }

  if (airport) {
    destinationTranslation = offersTranslations('serp.title.airport');
    searchQuery = new URLSearchParams({
      station_types: 'airport',
    }).toString();
  }

  if (railStation) {
    destinationTranslation = offersTranslations('serp.title.rail');
    searchQuery = new URLSearchParams({
      station_types: 'rail',
    }).toString();
  }

  return (
    <Body
      variant={{ kind: 'default' }}
      bodyTop={
        <StageLegacy.Stage
          content={stage.content}
          headline={primaryHeadline}
          variant={{ kind: 'default' }}
        />
      }
      searchData={stage?.content?.search}
      content={({ containerModule, contentModule }) => [
        containerModule(
          'usp',
          <BenefitsLegacy.Usp rating={globalRating.content} />,
        ),
        containerModule(
          'introduction-content',
          <ContentArticle
            modules={introduction?.segments ?? []}
            translations={translations}
          />,
          !!introduction?.segments,
        ),
        contentModule(
          'serpOffers',
          !!offersByCategory.content && (
            <SerpOffersModule
              content={offersByCategory.content}
              translations={{
                airConditioning: offersTranslations('car.airConditioning'),
                carTypes: t.raw('offers.car.type' as any),
                prevText: offersTranslations('prevText'),
                nextText: offersTranslations('nextText'),
                ariaLabel: offersTranslations('ariaLabel'),
                searchButtonText,
                disclaimertext: offersTranslations('disclaimerSerpOffers'),
                pricePrefix: offersTranslations('pricePrefix'),
                priceSuffix: offersTranslations('priceSuffix'),
                serpTitle: location.name
                  ? destinationTranslation.replace('%LOCATION%', location.name)
                  : defaultTitle,
                seats: offersTranslations('car.seats'),
                doors: offersTranslations('car.doors'),
                transmission: offersTranslations('car.transmission'),
                transmissionAutomatic: offersTranslations(
                  'car.transmissionAutomatic',
                ),
                transmissionManual: offersTranslations(
                  'car.transmissionManual',
                ),
              }}
              searchQuery={searchQuery}
              isAirport={!!airport}
            />
          ),
          !!offersByCategory.content &&
            Object.keys(offersByCategory.content).length > 0,
        ),
        containerModule(
          'article',
          <ContentArticle
            modules={article.segments}
            translations={translations}
            searchQuery={searchQuery}
          />,
        ),
        containerModule(
          'linkLists',
          <ContentArticle
            modules={links.content ?? []}
            translations={translations}
          />,
          !!links.content.length,
        ),
      ]}
      {...commonProps}
    />
  );
};

export { GeneralContent };
